import cx from "classnames";
import { useState } from "react";

import { CustomChartFilter } from "./CustomChartFilter";
import { FilterSettingsPopover } from "./FilterSettingsPopover";
import { InteractiveFilter } from "./InteractiveFilters/InteractiveFilter";
import { SavedFilters } from "./SavedFilters";
import { SharedFilters } from "./SharedFilters";
import type { SharedFilterDoc } from "./useSharedFilterController";
import { Tabs, type Tab } from "@theorgchartdev/storybook";
import { planningTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";
import { useBlockerContext } from "@/providers/BlockerProvider";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { appstr } from "@coreapp/utils/appstr";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { useFilterController } from "./useFilterController";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { useTopic } from "@/hooks/useTopic";

type Props = {
  doc: QXDoc;
  filterCtrl: ReturnType<typeof useFilterController>;
  canShowInteractiveFilter?: boolean;
  onEditSharedFilter?: (filter: SharedFilterDoc) => void;
  onViewFilterDetails?: (data: any) => void;
  initialTab?: number;
  onOpenEditClosure?: (tabIndex: number) => () => void;
};

export const ChartFilter = (props: Props) => {
  const { shouldBlock } = useBlockerContext();
  const { setModeDirty } = useRightSidebarContext();

  const [currentTab, setCurrentTab] = useState(props.initialTab ?? 0);
  const [sharedActiveCount, setSharedActiveCount] = useState(0);
  const [ownerActiveCount, setOwnerActiveCount] = useState(0);

  const handleTabChange = (index: number, ignoreBlocker = false) => {
    if (!ignoreBlocker) {
      const onProceed = () => handleTabChange(index, true);
      if (shouldBlock("rightSidebarChangeMode", onProceed)) {
        return;
      }
    }

    setModeDirty(false);
    setCurrentTab(index);
  };

  useTopic("chartDocumentClosed", () => {
    setSharedActiveCount(0);
    setOwnerActiveCount(0);
    setCurrentTab(0);
  });

  const getTabs = () => {
    const filterTabs: Tab[] = [];

    if (props.doc.IsCustomChart()) {
      const iwiz = props.doc.GetIWizData();
      const hasCCFilter = iwiz?.GetFilterData() && iwiz?.GetFilterRule() != null;
      filterTabs.push({
        header: appstr("Chart scope"),
        content: <CustomChartFilter filter={iwiz?.GetFilterRule() ?? null} />,
        dataTestId: "rsb-filters-myfilters",
        badgeProps: hasCCFilter
          ? {
              badgeContent: 1,
              color: "success",
              tooltipProps: {
                message: appstr("Number of entity applied to the chart", {
                  entity: appstr("Filters").toLowerCase()
                }),
                position: "top-start"
              }
            }
          : undefined
      });
    }

    const myFiltersTabIndex = props.canShowInteractiveFilter ? filterTabs.length : 0;
    if (props.canShowInteractiveFilter) {
      const numberOfInterFilters =
        (props.doc.GetFilterMgr()?.NumberOfActiveInterFilters() || 0) + ownerActiveCount;
      filterTabs.push({
        header: appstr("My filters"),
        content: (
          <InteractiveFilter
            filterCtrl={props.filterCtrl}
            onEditSharedFilter={props.onEditSharedFilter}
            onOwnerActiveCountChange={setOwnerActiveCount}
            onViewDetails={data =>
              props.onViewFilterDetails?.({ ...data, returnTab: myFiltersTabIndex })
            }
            onOpenEdit={props.onOpenEditClosure?.(myFiltersTabIndex)}
          />
        ),
        dataTestId: planningTestId("filter", "myfilters-tab") ?? "rsb-filters-interactive",
        badgeProps: numberOfInterFilters
          ? {
              badgeContent: numberOfInterFilters.toString(),
              color: "success",
              tooltipProps: {
                message: appstr("Number of entity applied to the chart", {
                  entity: appstr("Interactive filters").toLowerCase()
                }),
                position: "top-start"
              }
            }
          : undefined
      });
    }

    // Shared filters tab (feature-gated)
    if (Globals._uiConfig.fShareFilters) {
      const sharedTabIndex = filterTabs.length;
      filterTabs.push({
        header: appstr("Shared"),
        content: (
          <SharedFilters
            onActiveCountChange={setSharedActiveCount}
            onEditSharedFilter={props.onEditSharedFilter}
            onCopyFilter={(definition: any) => {
              (props.filterCtrl as any).handleCopyBlock(definition);
              setCurrentTab(myFiltersTabIndex);
            }}
            onCopyAndEditFilter={(definition: any) => {
              (props.filterCtrl as any).handleCopyAndEditBlock(definition);
              setCurrentTab(myFiltersTabIndex);
            }}
            onViewDetails={data =>
              props.onViewFilterDetails?.({ ...data, returnTab: sharedTabIndex })
            }
            onOpenEdit={props.onOpenEditClosure?.(sharedTabIndex)}
          />
        ),
        dataTestId: planningTestId("filter", "shared-tab") ?? "rsb-filters-shared",
        badgeProps: sharedActiveCount
          ? {
              badgeContent: sharedActiveCount.toString(),
              color: "success",
              tooltipProps: {
                message: appstr("Number of entity applied to the chart", {
                  entity: appstr("Shared filters").toLowerCase()
                }),
                position: "top-start"
              }
            }
          : undefined
      });
    }

    // Legacy (Saved) filters — only show tab when saved filters exist
    const numberOfSavedFilters = props.doc.GetFilterMgr()?.NumberOfActiveSavedFilters();
    const savedFilterCount = props.doc.GetFilterMgr()?.GetNumFilters() ?? 0;
    const hasSavedFilters = getFeatureAccess(enumFeaturesList.savedFilters);
    if (hasSavedFilters && savedFilterCount > 0) {
      filterTabs.push({
        header: appstr("Legacy"),
        content: <SavedFilters />,
        disabled: !hasSavedFilters,
        dataTestId: planningTestId("filter", "saved-tab") ?? "rsb-filters-saved",
        badgeProps: numberOfSavedFilters
          ? {
              badgeContent: numberOfSavedFilters.toString(),
              color: "success",
              tooltipProps: {
                message: appstr("Number of entity applied to the chart", {
                  entity: appstr("Saved filters").toLowerCase()
                }),
                position: "top-start"
              }
            }
          : undefined
      });
    }

    return filterTabs;
  };

  const tabs = getTabs();

  return (
    <div
      className={cx(
        "ocn-chart-explorer-column",
        "ocn-chart-explorer-tabular",
        "ocn-filter-chart-tab"
      )}
    >
      <FilterSettingsPopover />
      {tabs.length > 0 && (
        <Tabs
          tabSize={"medium"}
          customClass={"ocn-sidebar-tabs"}
          customHeaderClass={"oc-tab-header-no-divider"}
          customContentClass={"ocn-chart-explorer-tabs-content"}
          tabIndex={currentTab}
          onTabChange={handleTabChange}
          tabs={tabs}
        />
      )}
    </div>
  );
};
